import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';

export const useSubscription = () => {
  const {  user, activeAccountId } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const targetId = activeAccountId || user?.id;

  // Função para buscar dados da assinatura
  const fetchSubscription = useCallback(async () => {
    if (!targetId) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', targetId)
        .single();

      if (fetchError) {
        // Se não encontrou registro, criar um manualmente
        if (fetchError.code === 'PGRST116') {
          console.log('Usuário sem assinatura, criando registro manual...');
          
          const { data: newSubscription, error: insertError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: targetId,
              trial_start_date: new Date().toISOString(),
              trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              payment_status: 'trial',
              is_active: true
            })
            .select()
            .single();

          if (insertError) {
            throw insertError;
          }

          setSubscription(newSubscription);
          return;
        }
        throw fetchError;
      }

      setSubscription(data);
    } catch (err) {
      console.error('Erro ao buscar assinatura:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  // Função para verificar se o usuário tem acesso
  const hasAccess = useCallback(() => {
    if (!subscription) {
      console.log('useSubscription: No subscription data');
      return false;
    }

    // Se o admin bloqueou o acesso manualmente
    if (subscription.is_active === false) return false;

    const now = new Date();
    
    // console.log('useSubscription Debug:', {
    //   payment_status: subscription.payment_status,
    //   trial_end_date: subscription.trial_end_date,
    //   subscription_end_date: subscription.subscription_end_date,
    //   now: now.toISOString()
    // });
    
    // Se está no período de teste
    if (subscription.payment_status === 'trial') {
      const trialEnd = new Date(subscription.trial_end_date);
      const hasAccess = now <= trialEnd;
      // console.log('Trial check:', {
      //   now: now.toISOString(),
      //   trialEnd: trialEnd.toISOString(),
      //   hasAccess
      // });
      return hasAccess;
    }
    
    // Se tem assinatura ativa
    if (subscription.payment_status === 'active') {
      const subscriptionEnd = new Date(subscription.subscription_end_date);
      const hasAccess = now <= subscriptionEnd;
      // console.log('Active subscription check:', { now: now.toISOString(), subscriptionEnd: subscriptionEnd.toISOString(), hasAccess });
      return hasAccess;
    }
    
    // Se expirou
    // console.log('Subscription expired or unknown status');
    return false;
  }, [subscription]);

  // Função para verificar se está no período de teste
  const isInTrial = useCallback(() => {
    if (!subscription) return false;
    return subscription.payment_status === 'trial' && hasAccess();
  }, [subscription, hasAccess]);

  // Função para verificar se precisa pagar
  const needsPayment = useCallback(() => {
    if (!subscription) return true;
    return !hasAccess();
  }, [subscription, hasAccess]);

  // Função para obter dias restantes
  const getDaysRemaining = useCallback(() => {
    if (!subscription) return 0;

    const now = new Date();
    let endDate;

    if (subscription.payment_status === 'trial') {
      endDate = new Date(subscription.trial_end_date);
    } else if (subscription.payment_status === 'active') {
      endDate = new Date(subscription.subscription_end_date);
    } else {
      return 0;
    }

    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }, [subscription]);

  // Função para obter status da assinatura
  const getSubscriptionStatus = useCallback(() => {
    if (!subscription) return 'unknown';

    if (hasAccess()) {
      return subscription.payment_status === 'trial' ? 'trial' : 'active';
    } else {
      return 'expired';
    }
  }, [subscription, hasAccess]);

  // Carregar dados quando o usuário muda
  useEffect(() => {
    if (targetId) {
      fetchSubscription();
    }
  }, [targetId, fetchSubscription]);

  return {
    subscription,
    loading,
    error,
    hasAccess,
    isInTrial,
    needsPayment,
    getDaysRemaining,
    getSubscriptionStatus,
    refreshSubscription: fetchSubscription
  };
};

